'use client';

/**
 * KpiTile — the shared visual primitive for mode9's System Obeya KPI
 * cards. Each KPI card in the preset is a thin shell that calls
 * useSystemObeyaView, slices its 2 fields off response.totals, and
 * renders one of these tiles.
 *
 * Typography from `useTypographyBundle('canvas_dashboard')` — KPI
 * heroes use the `amount` slot (text-4xl font-bold text-primary
 * tabular-nums). Container chrome — icon chip, sub-value chip,
 * outer card padding, border-radius — lives in this file per the
 * mode7 doctrine (typography in the registry; padding / chips /
 * icon sizes are render concerns).
 *
 * Reference: legacy `system-obeya-widget.tsx` StatCard.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { type LucideIcon } from 'lucide-react';
import type { QueryKey } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport, paintedKpi } from '@/lib/design/card-export';

interface KpiTileProps {
  label: string;
  Icon: LucideIcon;
  value: string;
  sub?: string;
  /** Scene-mirror identity — backend @card content_key. */
  contentKey?: string;
  /** React Query key whose data feeds this tile. Drives
   *  capture-envelope derivation; threaded from each KPI card. */
  queryKey: QueryKey;
}

export function KpiTile({ label, Icon, value, sub, contentKey, queryKey }: KpiTileProps) {
  const T = useCardTypography();

  // Every KPI card delegates here; one registration covers all 8.
  // `paintedKpi` enforces painted-string output (scene = what the
  // user saw) at the type level — no hand-rolled `{json: {...}}`
  // means no opportunity to ship raw numerics, and `query_key`
  // can't be silently omitted.
  useCardExport(paintedKpi({
    label,
    value,
    sub,
    content_key: contentKey,
    query_key: queryKey,
  }));
  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-sm h-full flex flex-col group hover:border-primary/30 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2.5 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
          <Icon className="w-5 h-5" />
        </div>
        {sub && (
          <span className={cn(T.metadata, 'font-medium bg-muted px-2 py-1 rounded-md')}>
            {sub}
          </span>
        )}
      </div>
      <p className={cn(T.amount)}>{value}</p>
      <p className={cn(T.body, 'text-muted-foreground mt-1 font-medium')}>{label}</p>
    </div>
  );
}

export function fmt(n: unknown): string {
  if (n == null || typeof n !== 'number') return '—';
  return n.toLocaleString();
}
